const auxDevEnvironment =
  import.meta.env.VITE_NODE_ENV === undefined || import.meta.env.VITE_NODE_ENV === 'development'
    ? true
    : false;
const auxStagingEnvironment = import.meta.env.VITE_NODE_ENV === 'staging' ? true : false;

const auxBackendProdURL = 'https://backend.kincode.app';
const auxBackendStagingURL = 'https://dev-backend.kincode.app';
const auxBackendDevURL = String(import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001').trim();
const auxBackendURL = auxDevEnvironment
  ? auxBackendDevURL
  : auxStagingEnvironment
    ? auxBackendStagingURL
    : auxBackendProdURL;

const companyColor = '#BBF7D0'; // Green-200
const companySecondaryColor = '#BFC6FF'; // Purple
const companyTerciaryColor = '#FFBF76'; // Yellow

const Configs = {
  // ------------------------------------------------------------------------------------------
  // Environment information
  // ------------------------------------------------------------------------------------------
  devEnvironment: auxDevEnvironment,
  stagingEnvironment: auxStagingEnvironment,

  // ------------------------------------------------------------------------------------------
  // URIs
  // ------------------------------------------------------------------------------------------
  backendProdURL: auxBackendProdURL,
  backendStagingURL: auxBackendStagingURL,
  backendDevURL: auxBackendDevURL,

  backendURL: auxDevEnvironment
    ? auxBackendDevURL
    : auxStagingEnvironment
      ? auxBackendStagingURL
      : auxBackendProdURL,

  newCompanyOnboardingURL: auxDevEnvironment
    ? 'http://localhost:3000/customerOnboarding/'
    : 'https://platform.kincode.app/customerOnboarding/',
  /**
   * Academy article URL for Organigram “Review roles” help (opens in a new tab).
   * Set `VITE_ORGANIGRAM_ROLES_ACADEMY_URL` in the environment; leave unset to hide the help icon.
   */
  organigramRolesAcademyArticleURL: String(import.meta.env.VITE_ORGANIGRAM_ROLES_ACADEMY_URL || '').trim(),
  // ------------------------------------------------------------------------------------------
  // Crypto
  // ------------------------------------------------------------------------------------------
  privateKey: import.meta.env.VITE_PRIVATE_KEY,

  // ------------------------------------------------------------------------------------------
  // SecurityLevel
  // ------------------------------------------------------------------------------------------
  securityLevelIC: 1,
  securityLevelInterviewer: 2,
  securityLevelManager: 3,
  securityLevelHR: 5,
  securityLevelExec: 90,
  securityLevelAdmin: 99,
  /** Aligns with backend `Configs.securityLevelHRBP` / `platform-roles` securityLevel. */
  securityLevelHRBP: 4,
  securityLevelSuperAdmin: 99999,

  // ------------------------------------------------------------------------------------------
  // API endpoints
  // ------------------------------------------------------------------------------------------
  // Logging
  platformLogErrorAPI: auxBackendURL + '/api/platform/post/logError/',
  // Authentication
  loginAPI: auxBackendURL + '/api/authentication/post/login/',
  authenticate: auxBackendURL + '/api/authentication/post/authenticate/',
  getKincodeTokenAPI: auxBackendURL + '/api/authentication/post/authenticate/getKincodeToken',
  updatePasswordAPI: auxBackendURL + '/api/authentication/post/authenticate/updatepwd',
  resetPasswordAPI: auxBackendURL + '/api/authentication/post/authenticate/resetpwd',
  logoutAPI: auxBackendURL + '/api/authentication/post/logout/',

  // Admin
  platformAdminGetUserActivityAPI: auxBackendURL + '/api/platform/admin/get/userActivity/',
  platformAdminGetAllCompaniesAPI: auxBackendURL + '/api/platform/admin/get/allCompanies/',
  platformAdminGetCompanySummaryAPI: auxBackendURL + '/api/platform/admin/get/companySummary/',
  platformAdminGetAgreementTemplatesAPI: auxBackendURL + '/api/platform/admin/get/agreementTemplates/',
  platformAdminPostCompanyDefaultProjectAPI: auxBackendURL + '/api/platform/admin/post/company/defaultProject/',
  platformAdminPostCompanyCloneAgreementAPI:
    auxBackendURL + '/api/platform/admin/post/company/cloneAgreementFromTemplate/',
  platformAdminPostNewCompanyAPI: auxBackendURL + '/api/platform/admin/post/newCompany/',
  platformAdminGetSelfOnboardingCampaignsAPI:
    auxBackendURL + '/api/platform/admin/get/selfOnboardingCampaigns/',
  platformAdminGetSelfOnboardingCampaignAPI:
    auxBackendURL + '/api/platform/admin/get/selfOnboardingCampaign/',
  platformAdminPostSelfOnboardingCampaignAPI:
    auxBackendURL + '/api/platform/admin/post/selfOnboardingCampaign/',
  platformAdminPutSelfOnboardingCampaignAPI:
    auxBackendURL + '/api/platform/admin/put/selfOnboardingCampaign/',
  platformGetCountriesAPI: auxBackendURL + '/api/platform/get/countries/',

  // AI Experiments
  platformAdminAIExperimentsPipelineAPI:
    auxBackendURL + '/api/platform/admin/aiExperiments/pipeline/',
  platformGetPulseSurveyAISummaryAPI: auxBackendURL + '/api/platform/get/pulseSurveyAISummary/',
  platformPOSTOnboardingSurveysAISummaryAPI:
    auxBackendURL + '/api/platform/post/onboardingSurveysAISummary/',

  // CCOaaS
  platformGetCCOaaSReportAPI: auxBackendURL + '/api/platform/get/CCOaaSReport/',
  platformGetCCOaaSCompanyDetailsAIAnalysisAPI:
    auxBackendURL + '/api/platform/get/CCOaaSCompanyDetailsAIAnalysis/',

  // Profile and employee info
  platformGetProfileAPI: auxBackendURL + '/api/platform/get/profile/',
  platformGetJobInfoAPI: auxBackendURL + '/api/platform/get/jobInfo/',
  platformGetJobCompAPI: auxBackendURL + '/api/platform/get/jobComp/',
  platformPostProfilePictureAPI: auxBackendURL + '/api/platform/post/profilePicture/',

  // Compliance
  platformGetComplianceCatalogAPI: auxBackendURL + '/api/platform/compliance/catalog',
  platformGetComplianceCompanyAPI: auxBackendURL + '/api/platform/compliance/company',
  platformPutComplianceJurisdictionsAPI:
    auxBackendURL + '/api/platform/compliance/company/jurisdictions',
  platformGetComplianceScoreAPI: auxBackendURL + '/api/platform/compliance/score',
  platformGetCompliancePoliciesAPI: auxBackendURL + '/api/platform/compliance/policies',
  platformGetCompliancePolicyItemAPI: auxBackendURL + '/api/platform/compliance/policies/item',
  platformPutCompliancePolicyDraftAPI: auxBackendURL + '/api/platform/compliance/policies/draft',
  platformPostComplianceCompanyPolicyAPI:
    auxBackendURL + '/api/platform/compliance/policies/company',
  platformPostComplianceGenerateDraftAPI:
    auxBackendURL + '/api/platform/compliance/policies/generate-draft',
  platformPostCompliancePublishAPI: auxBackendURL + '/api/platform/compliance/policies/publish',
  platformGetComplianceMyDocumentsAPI: auxBackendURL + '/api/platform/compliance/my-documents',

  // Inbox
  platformPostInboxRequestAPI: auxBackendURL + '/api/platform/post/inboxRequest/',
  platformGetInboxRequestAPI: auxBackendURL + '/api/platform/get/inboxRequest/',
  platformPutInboxRequestAPI: auxBackendURL + '/api/platform/put/inboxRequest/',
  platformGetInboxMessagesForEmployeeAPI: auxBackendURL + '/api/platform/get/inboxMessages/',
  platformGetInboxMessagesForCompanyAPI: auxBackendURL + '/api/platform/get/allInboxMessages/',
  platformDeleteInboxMessageAPI: auxBackendURL + '/api/platform/delete/inboxMessage/',

  // Adoption
  platformGetAdoptionCalendarAPI: auxBackendURL + '/api/platform/get/adoptionCalendar',
  platformGetAdoptionConfigAPI: auxBackendURL + '/api/platform/get/adoptionConfig',
  platformPutAdoptionConfigAPI: auxBackendURL + '/api/platform/put/adoptionConfig',

  // Holidays
  platformGetTimeOffPerEmployeeAPI: auxBackendURL + '/api/platform/get/timeOffPerEmployee/',
  platformGetTimeOffPerTargetEmployeeIDAPI:
    auxBackendURL + '/api/platform/get/timeOffPerTargetEmployeeID/',
  platformGetTimeOffAPI: auxBackendURL + '/api/platform/get/timeoff/',
  platformPostTimeOffAPI: auxBackendURL + '/api/platform/post/timeoff/',
  platformPostTimeOffValidateAPI: auxBackendURL + '/api/platform/post/timeoff/validate/',
  platformPutTimeOffAPI: auxBackendURL + '/api/platform/put/timeoff/',
  platformDeleteTimeOffAPI: auxBackendURL + '/api/platform/delete/timeoff/',
  platformGetTimeOffPendingRequestsForManagerAPI:
    auxBackendURL + '/api/platform/get/timeOffPendingRequests/',
  platformGetDaysOffHRReportAPI: auxBackendURL + '/api/platform/get/daysOffHRReport/',
  platformGetTeamHolidayCalendarAPI: auxBackendURL + '/api/platform/get/teamHolidayCalendar/',
  platformPutTimeOffAwardTimeOffToEmployeeAPI:
    auxBackendURL + '/api/platform/put/timeOff/awardTimeOffToEmployee/',
  platformGetTimeOffRequestRemainingDaysAPI:
    auxBackendURL + '/api/platform/get/timeOffRequestRemainingDays/',
  platformGetTimeOffAwardDetailsAPI: auxBackendURL + '/api/platform/get/timeOffAwardDetails/',

  // Punch v2
  platformGetPuchInOutCompanyProjectPerEmployeeAPI:
    auxBackendURL + '/api/platform/get/punchInOutCompanyProjectPerEmployee/',
  platformGetEmployeeContractAPI: auxBackendURL + '/api/platform/get/employee/contract/',
  platformPostPunchEmployeeTimesheetWeekAPI:
    auxBackendURL + '/api/platform/post/punchInOutCompanyEmployeeTimesheet/',
  platformGetPunchEmployeeTimesheetWeekAPI:
    auxBackendURL + '/api/platform/get/punchInOutCompanyEmployeeTimesheet/',
  platformPostPunchInOutCompanyEmployeeTimesheetSubmitForApprovalAPI:
    auxBackendURL + '/api/platform/post/punchInOutCompanyEmployeeTimesheetSubmitForApproval/',
  platformGetPunchInOutCompanyPendingTimesheetAPI:
    auxBackendURL + '/api/platform/get/punchInOutCompanyPendingTimesheet/',
  platformGetPunchInOutCompanyEmployeeTimesheetTimerAPI:
    auxBackendURL + '/api/platform/get/punchInOutCompanyEmployeeTimesheetTimer/',
  platformPostPunchInOutCompanyTimesheetTimerAPI:
    auxBackendURL + '/api/platform/post/punchInOutCompanyTimesheetTimer/',
  platformPostPunchInOutCompanyEmployeeTimesheetApproveRejectAPI:
    auxBackendURL + '/api/platform/post/punchInOutCompanyEmployeeTimesheetApproveReject/',
  platformPostPunchInOutCompanyEmployeeTimesheetReOpenAPI:
    auxBackendURL + '/api/platform/post/punchInOutCompanyEmployeeTimesheetReOpen/',
  platformPostPunchInOutCompanyTimesheetBulkApproveCompliantAPI:
    auxBackendURL + '/api/platform/post/punchInOutCompanyTimesheetBulkApproveCompliant',
  platformPostPunchInOutCompanyTimesheetBulkRejectNonCompliantAPI:
    auxBackendURL + '/api/platform/post/punchInOutCompanyTimesheetBulkRejectNonCompliant',
  platformGetCompanyFiltersAPI: auxBackendURL + '/api/platform/get/companyFilters/',

  // Filters module — paginated, scope-aware filter endpoints (IC+ access)
  platformGetFiltersEmployeesAPI: auxBackendURL + '/api/platform/filters/employees',
  platformGetFiltersManagersAPI: auxBackendURL + '/api/platform/filters/managers',
  platformGetFiltersAreasAPI: auxBackendURL + '/api/platform/filters/areas',
  platformGetFiltersSubareasAPI: auxBackendURL + '/api/platform/filters/subareas',
  platformGetFiltersTitlesAPI: auxBackendURL + '/api/platform/filters/titles',
  platformGetFiltersRolesAPI: auxBackendURL + '/api/platform/filters/roles',
  platformGetFiltersLevelsAPI: auxBackendURL + '/api/platform/filters/levels',
  platformGetFiltersTenureAPI: auxBackendURL + '/api/platform/filters/tenure',
  platformGetFiltersTypesAPI: auxBackendURL + '/api/platform/filters/types',
  platformGetFiltersLegalEntitiesAPI: auxBackendURL + '/api/platform/filters/legalEntities',
  platformGetFiltersLegalGendersAPI: auxBackendURL + '/api/platform/filters/legalGenders',
  platformGetFiltersContractTypesAPI: auxBackendURL + '/api/platform/filters/contractTypes',
  platformGetFiltersPlacesOfWorkAPI: auxBackendURL + '/api/platform/filters/placesOfWork',
  platformGetFiltersCountriesOfWorkAPI: auxBackendURL + '/api/platform/filters/countriesOfWork',
  platformGetFiltersCitiesOfWorkAPI: auxBackendURL + '/api/platform/filters/citiesOfWork',
  platformGetFiltersCountriesOfOperationsAPI:
    auxBackendURL + '/api/platform/filters/countriesOfOperations',
  platformPostCreateProjectAPI: auxBackendURL + '/api/platform/post/createProject/',
  platformPutUpdateProjectAPI: auxBackendURL + '/api/platform/put/updateProject/',
  platformDeleteProjectAPI: auxBackendURL + '/api/platform/delete/project/',
  platformPostAssignEmployeeToProjectAPI:
    auxBackendURL + '/api/platform/post/assignEmployeeToProject/',
  platformPostAssignEmployeesToProjectBulkAPI:
    auxBackendURL + '/api/platform/post/assignEmployeesToProjectBulk/',
  platformDeleteEmployeeFromProjectAPI: auxBackendURL + '/api/platform/delete/employeeFromProject/',
  platformGetCompanyProjectsAPI: auxBackendURL + '/api/platform/get/companyProjects/',
  platformGetEmployeesWithoutActiveProjectAPI:
    auxBackendURL + '/api/platform/get/employeesWithoutActiveProject/',
  platformGetPunchSettingsAPI: auxBackendURL + '/api/platform/get/punchSettings',
  platformPutPunchSettingsAPI: auxBackendURL + '/api/platform/put/punchSettings',

  // Company Agreements
  platformGetCompanyAgreementsAPI: auxBackendURL + '/api/platform/get/companyAgreements/',
  platformGetCompanyAgreementAPI: auxBackendURL + '/api/platform/get/companyAgreement/',
  platformGetCompanyAgreementTemplatesAPI:
    auxBackendURL + '/api/platform/get/companyAgreementTemplates/',
  platformPostCreateCompanyAgreementAPI: auxBackendURL + '/api/platform/post/createCompanyAgreement/',
  platformPostCloneCompanyAgreementFromTemplateAPI:
    auxBackendURL + '/api/platform/post/cloneCompanyAgreementFromTemplate/',
  platformPostUpdateCompanyAgreementAPI: auxBackendURL + '/api/platform/post/updateCompanyAgreement/',
  platformDeleteCompanyAgreementAPI: auxBackendURL + '/api/platform/delete/companyAgreement/',

  // Employee Contracts
  platformGetCompanyContractsAPI: auxBackendURL + '/api/platform/get/companyContracts/',
  platformGetEmployeesWithoutActiveContractAPI:
    auxBackendURL + '/api/platform/get/employeesWithoutActiveContract/',
  platformGetEmployeeContractsAPI: auxBackendURL + '/api/platform/get/employeeContracts/',
  platformPostCreateEmployeeContractAPI: auxBackendURL + '/api/platform/post/createEmployeeContract/',
  platformPostBulkCreateEmployeeContractsAPI:
    auxBackendURL + '/api/platform/post/bulkCreateEmployeeContracts/',
  platformPostUpdateEmployeeContractAPI: auxBackendURL + '/api/platform/post/updateEmployeeContract/',
  platformDeleteEmployeeContractAPI: auxBackendURL + '/api/platform/delete/employeeContract/',


  // Performance Review module
  platformGetLivePerformanceCampaignsAPI:
    auxBackendURL + '/api/platform/get/livePerformanceCampaigns/',
  platformPostPerformanceCampaignAPI: auxBackendURL + '/api/platform/post/performanceCampaign/',
  platformPostPerformanceCampaignEnterPreviewAPI:
    auxBackendURL + '/api/platform/post/performanceCampaign/enterPreview/',
  platformPostPerformanceCampaignExitPreviewAPI:
    auxBackendURL + '/api/platform/post/performanceCampaign/exitPreview/',
  platformPostPerformanceCampaignLaunchAPI:
    auxBackendURL + '/api/platform/post/performanceCampaign/launch/',
  platformPostPerformanceCampaignCloneAPI:
    auxBackendURL + '/api/platform/post/performanceCampaign/clone/',
  platformPostFeedbackProvidersAPI:
    auxBackendURL + '/api/platform/post/performanceSubmitProviders/',
  platformPostSelfFeedbackAPI: auxBackendURL + '/api/platform/post/performanceSubmitSelfFeedback/',
  platformPostManagerFeedbackAPI:
    auxBackendURL + '/api/platform/post/performanceSubmitManagerFeedback/',
  platformGetPerformanceFeedbackProvidersAPI:
    auxBackendURL + '/api/platform/get/performanceFeedbackProviders/',
  platformGetPerformanceFeedbacksAPI: auxBackendURL + '/api/platform/get/performanceFeedbacks/',
  platformDeleteFeedbackProviderAPI:
    auxBackendURL + '/api/platform/delete/performanceFeedbackProvider/',
  platformGetPerformanceDraftsAPI: auxBackendURL + '/api/platform/get/performanceDraft/',
  platformGetManagerPerformanceDraftsAPI:
    auxBackendURL + '/api/platform/get/managerPerformanceDraft/',
  platformGetPerformanceFeedbacksDraftsAPI:
    auxBackendURL + '/api/platform/get/performanceFeedbacksDrafts/',
  platformSubmitSelfFeedbackAPI:
    auxBackendURL + '/api/platform/post/performanceSubmitReviewedSelfFeedback/',
  platformSubmitManagerFeedbackAPI:
    auxBackendURL + '/api/platform/post/performanceSubmitReviewedManagerFeedback/',
  platformGetPerformanceReviewByStatusAPI:
    auxBackendURL + '/api/platform/get/performanceReviewByStatus/',
  platformGetPerformancePeerFeedbackAPI:
    auxBackendURL + '/api/platform/get/performanceFeedbackForPeer/',
  platformPostPeerFeedbackAPI: auxBackendURL + '/api/platform/post/submitPeerFeedback/',
  platformGetPerformanceReviewStatusByCompanyAPI:
    auxBackendURL + '/api/platform/get/livePerformanceReviewStatusByCompany/',
  platformGetPerformanceCampaignsHistoryAPI:
    auxBackendURL + '/api/platform/get/performanceCampaignsHistory/',
  platformGetSpecificPerformanceReviewAPI:
    auxBackendURL + '/api/platform/get/specificPerformanceReview/',
  platformGetPerformanceReviewCampaignsByCompanyAPI:
    auxBackendURL + '/api/platform/get/performanceReviewCampaignsByCompany/',
  platformGetPerformanceReviewReportingDataPerCampaignAPI:
    auxBackendURL + '/api/platform/get/performanceReviewReportingDataPerCampaign/',
  platformGetCSVDownloadPerformanceCampaignAPI:
    auxBackendURL + '/api/platform/get/performanceReviewCSV/',

  // Calibration module
  platformGetCalibrationCampaignsAPI: auxBackendURL + '/api/platform/get/calibrationCampaigns/',
  platformGetCalibrationCampaignsListAPI:
    auxBackendURL + '/api/platform/get/calibrationCampaignsList/',
  platformGetCalibrationCampaignCsvAPI: auxBackendURL + '/api/platform/get/calibrationCampaignCsv/',
  platformPostCalibrationCampaignAPI: auxBackendURL + '/api/platform/post/calibrationCampaign/',
  platformGetCalibrationExistingRatingsForEmployeeAPI:
    auxBackendURL + '/api/platform/get/calibrationExistingRatingsForEmployee/',
  platformGetCalibrationLastFinalizedForEmployeeAPI:
    auxBackendURL + '/api/platform/get/calibrationLastFinalizedForEmployee/',
  platformGetCalibrationRosterAPI: auxBackendURL + '/api/platform/get/calibrationRoster/',
  platformGetEmployeeVisibilityScopeAPI: auxBackendURL + '/api/platform/get/employeeVisibilityScope/',
  platformGetEmployeeVisibilityScopeCapabilitiesAPI:
    auxBackendURL + '/api/platform/get/employeeVisibilityScopeCapabilities/',
  platformGetEmployeeVisibilityScopeFiltersAreasAPI:
    auxBackendURL + '/api/platform/get/employeeVisibilityScopeFiltersAreas/',
  platformGetEmployeeVisibilityScopeFiltersSubareasAPI:
    auxBackendURL + '/api/platform/get/employeeVisibilityScopeFiltersSubareas/',
  platformGetEmployeeVisibilityScopeFiltersEmployeesAPI:
    auxBackendURL + '/api/platform/get/employeeVisibilityScopeFiltersEmployees/',
  platformGetEmployeeVisibilityScopeFiltersManagersAPI:
    auxBackendURL + '/api/platform/get/employeeVisibilityScopeFiltersManagers/',
  platformPostCalibrationRatingForEmployeeAPI:
    auxBackendURL + '/api/platform/post/calibrationRatingForEmployee/',
  platformGetCalibrationSessionFilterFacetsAPI:
    auxBackendURL + '/api/platform/get/calibrationSessionFilterFacets/',
  platformGetCalibrationPublicResultForPerformanceCampaignAPI:
    auxBackendURL + '/api/platform/get/calibrationPublicResultForPerformanceCampaign/',

  // Meetings
  platformPostMeetingAPI: auxBackendURL + '/api/platform/post/submitMeeting/',
  platformGetOneOnOneHistoryManagerAPI:
    auxBackendURL + '/api/platform/get/oneOnOneHistoryPerManager/',
  platformGetMeetingAPI: auxBackendURL + '/api/platform/get/meeting/',
  platformGetOneOnOnesReportAPI: auxBackendURL + '/api/platform/get/oneOnOnesReport/',
  platformGetOneOnOnesReportMetricsAPI: auxBackendURL + '/api/platform/get/oneOnOnesReportMetrics/',
  platformGetOneOnOneTeamAPI: auxBackendURL + '/api/platform/get/oneOnOneTeam/',
  platformDeleteMeetingAPI: auxBackendURL + '/api/platform/delete/meeting/',

  // Pending to-do's
  platformGetPendingToDosAPI: auxBackendURL + '/api/platform/get/pendingToDos/',
  platformPutUpdateNotificationStatus:
    auxBackendURL + '/api/platform/put/updateNotificationStatus/',

  // Pulse module
  pulseLiveCampaignsPerManagerAPI: auxBackendURL + '/api/pulse/get/liveCampaignsPerManager/',
  pulseLiveCampaignsAdminAPI: auxBackendURL + '/api/pulse/get/liveCampaignsAdmin/',
  platformGetPulseHomeGraphFrontendDataAPI:
    auxBackendURL + '/api/platform/get/pulseHomeGraphFrontendData/',
  platformGetPulseKeyIndicatorsAPI: auxBackendURL + '/api/platform/post/pulseKeyIndicators/',
  platformPostPulseHomeEngagementEvolutionAPI:
    auxBackendURL + '/api/platform/post/pulseHomeEngagementEvolution/',
  platformPostPulseHomeEngagementMonthComparisonAPI:
    auxBackendURL + '/api/platform/post/pulseHomeEngagementMonthComparison/',
  platformPostPulseHomeKeyIndicatorCardAPI:
    auxBackendURL + '/api/platform/post/pulseHomeKeyIndicatorCard/',
  platformPostPulseCompareValidateSegmentsAPI:
    auxBackendURL + '/api/platform/post/pulseCompareValidateSegments/',
  platformPostPulseCompareAPI: auxBackendURL + '/api/platform/post/pulseCompare/',
  platformGetPulseComparisonsAPI: auxBackendURL + '/api/platform/get/pulseComparisons/',
  platformPostPulseComparisonSaveAPI: auxBackendURL + '/api/platform/post/pulseComparisonSave/',
  platformPostPulseComparisonHistoryAPI: auxBackendURL + '/api/platform/post/pulseComparisonHistory/',
  platformPatchPulseComparisonAPI: auxBackendURL + '/api/platform/patch/pulseComparison/',
  platformDeletePulseComparisonAPI: auxBackendURL + '/api/platform/delete/pulseComparison/',
  platformPostPulseHomeAvailableFiltersAPI:
    auxBackendURL + '/api/platform/post/pulseHomeAvailableFilters/',
  platformPostPulseFiltersManagersAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersManagers/',
  platformPostPulseFiltersAreasAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersAreas/',
  platformPostPulseFiltersSubareasAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersSubareas/',
  platformPostPulseFiltersLevelsAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersLevels/',
  platformPostPulseFiltersCountriesAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersCountries/',
  platformPostPulseFiltersEmployeeTypesAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersEmployeeTypes/',
  platformPostPulseFiltersQuestionDriverAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersQuestionDriver/',
  platformPostPulseFiltersTenureAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersTenure/',
  platformPostPulseFiltersIsActiveEmployeeAPI:
    auxBackendURL + '/api/platform/post/pulseFiltersIsActiveEmployee/',
  pulseGetAllQuestionsAPI: auxBackendURL + '/api/pulse/get/allQuestions/',
  platformUnauthenticatedPulseGetSurveyAPI: auxBackendURL + '/api/pulse/get/liveSurvey/',
  platformAuthenticatedPulseGetSurveyAPI:
    auxBackendURL + '/api/pulse/get/authenticatedLiveSurvey/',
  platformAuthenticatedPulsePostSurveyAnswersAPI:
    auxBackendURL + '/api/pulse/post/authenticatedSurveyAnswers/',
  pulsePostSurveyAnswerAPI: auxBackendURL + '/api/pulse/post/surveyAnswers/',
  platformGetCSVDownloadCampaignAPI: auxBackendURL + '/api/pulse/get/campaignCSV/',
  platformGetPulseSurveyReminderPreviewAPI:
    auxBackendURL + '/api/pulse/get/surveyReminderPreview/',
  platformPostPulseSurveyReminderConfirmAPI:
    auxBackendURL + '/api/pulse/post/surveyReminderConfirm/',
  platformGetPulseCampaignsPerCompany: auxBackendURL + '/api/pulse/get/campaignsPerCompany/',
  platformGetPulseCampaignIDAPI: auxBackendURL + '/api/pulse/get/campaignID/',
  platformGetPulseCampaignQuestionDriverDistributionAPI:
    auxBackendURL + '/api/pulse/get/campaignID/driverDistribution/',
  platformGetPulseDeliveryChannelsAPI: auxBackendURL + '/api/pulse/get/deliveryChannels/',
  platformGetPulseCampaignIDAnswersDirectsAPI: auxBackendURL + '/api/pulse/get/campaignIDAnswers/',
  platformGetPulseCampaignIDAnswersManagersAPI:
    auxBackendURL + '/api/pulse/get/campaignIDAnswersManagers/',
  platformGetPulseCampaignIDAnswersOrgAPI: auxBackendURL + '/api/pulse/get/campaignIDAnswersOrg/',
  platformPostPulseCampaignAPI: auxBackendURL + '/api/pulse/post/createPulseCampaign/',
  platformUpdatePulseCampaignAPI: auxBackendURL + '/api/pulse/post/updatePulseCampaign/',
  platformPatchPulseCampaignNextQuestionAPI:
    auxBackendURL + '/api/pulse/campaigns/nextQuestion/',
  platformDeletePulseCampaignAPI: auxBackendURL + '/api/pulse/delete/deletePulseCampaign/',
  platformPostCreateQuestionAPI: auxBackendURL + '/api/pulse/post/createQuestion/',
  platformPutUpdateQuestionAPI: auxBackendURL + '/api/pulse/put/updateQuestion/',
  pulseGetPulseDailyCheckAPI: auxBackendURL + '/api/pulse/get/pulseDailyCheck/',
  platformPulseGetDailyQuestionAPI: auxBackendURL + '/api/pulse/get/dailyQuestion/',
  pulseAnswerAPI: auxBackendURL + '/api/pulse/post/employeeAnswer/',
  pulseAnswerAuthenticatedAPI: auxBackendURL + '/api/pulse/post/authenticatdEmployeeAnswer/',
  pulseQuestionsReportingDataAPI: auxBackendURL + '/api/pulse/post/questionsReportingData/',
  pulseCommentsReportingDataAPI:
    auxBackendURL + '/api/platform/post/pulseCommentsReportingData/',
  pulseCommentsReportingExportAPI:
    auxBackendURL + '/api/platform/post/pulseCommentsReportingExport/',
  platformGetPulseQuestionProfileAPI: auxBackendURL + '/api/pulse/get/questionProfile/',
  platformPostPulseCommentsAPI: auxBackendURL + '/api/platform/post/pulseComments/',
  platformPostPulseProfileQuestionsAPI: auxBackendURL + '/api/platform/post/pulseProfileQuestions/',
  platformPostPulseProfileBreakdownAPI: auxBackendURL + '/api/platform/post/pulseProfileBreakdown/',
  platformPostPulseCampaignProfileBreakdownAPI:
    auxBackendURL + '/api/platform/post/pulseCampaignProfileBreakdown/',
  platformGetPulseDriverProfileAPI: auxBackendURL + '/api/pulse/get/driverProfile/',
  platformGetPulseDriversReportingAPI: auxBackendURL + '/api/platform/post/pulseDriversReporting/',
  platformGetPulseResponseRateReportingAPI:
    auxBackendURL + '/api/platform/post/pulseResponseRateReporting/',
  platformGetPulseIndividualEmployeesEngagementAPI:
    auxBackendURL + '/api/platform/post/pulseIndividualEmployeesEngagement/',
  platformPostPulseInitiateAnonymousConversationAPI:
    auxBackendURL + '/api/platform/post/pulseInitiateAnonymousConversation/',
  platformGetPulseAvailableFiltersAPI: auxBackendURL + '/api/platform/get/pulseAvailableFilters/',
  platformGetCompanyEngagementAPI: auxBackendURL + '/api/platform/post/companyEngagement/',
  platformGetCompanyDriversAPI: auxBackendURL + '/api/platform/post/companyDrivers/',

  // Recognition
  platformGetRecognitionDataForCompanyPageAPI:
    auxBackendURL + '/api/platform/get/recognitionDataForCompanyPage',
  platformGetRecognitionLeaderboardsForCompanyPageAPI:
    auxBackendURL + '/api/platform/get/recognitionLeaderboardsForCompanyPage',
  platformGetRecognitionPersonalSummaryAPI:
    auxBackendURL + '/api/platform/get/recognitionPersonalSummary/',
  platformPostRecognitionReportingSummaryAPI:
    auxBackendURL + '/api/platform/post/recognitionReportingSummary/',
  platformPostRecognitionReportingSegmentAPI:
    auxBackendURL + '/api/platform/post/recognitionReportingSegment/',
  platformPostRecognitionReportingAreaHighlightsAPI:
    auxBackendURL + '/api/platform/post/recognitionReportingAreaHighlights/',
  platformPostRecognitionReportingByValuesAPI:
    auxBackendURL + '/api/platform/post/recognitionReportingByValues/',
  platformPostRecognitionReportingManagerAdoptionAPI:
    auxBackendURL + '/api/platform/post/recognitionReportingManagerAdoption/',
  platformPostRecognitionReportingEmployeeSpotlightsAPI:
    auxBackendURL + '/api/platform/post/recognitionReportingEmployeeSpotlights/',
  platformPostRecognitionReportingNetworkAPI:
    auxBackendURL + '/api/platform/post/recognitionReportingNetwork/',
  platformGetRecognitionReportingEmployeesAPI:
    auxBackendURL + '/api/platform/get/recognitionReportingEmployees',
  platformPostRecognitionAPI: auxBackendURL + '/api/platform/post/submitRecognition/',
  platformGetPublicSingleRecognitionAPI:
    auxBackendURL + '/api/platform/get/publicSingleRecognition/',
  platformGetPublicTrustCenterAPI: auxBackendURL + '/api/platform/get/publicTrustCenter/',
  platformGetPublicTrustCenterStatementAPI:
    auxBackendURL + '/api/platform/get/publicTrustCenterStatement/',
  platformGetPublicCareersAPI: auxBackendURL + '/api/platform/get/publicCareers/',
  platformGetPublicCareersJobAPI: auxBackendURL + '/api/platform/get/publicCareersJob/',
  platformPostPublicCareersApplyAPI: auxBackendURL + '/api/platform/post/publicCareersApply/',
  platformPostSelfOnboardingSsoStartAPI:
    auxBackendURL + '/api/platform/post/selfOnboardingSsoStart/',
  platformPostSelfOnboardingRegisterAPI:
    auxBackendURL + '/api/platform/post/selfOnboardingRegister/',
  platformPostSelfOnboardingResendAPI: auxBackendURL + '/api/platform/post/selfOnboardingResend/',
  platformGetSelfOnboardingCampaignLanguageAPI:
    auxBackendURL + '/api/platform/get/selfOnboardingCampaignLanguage/',
  platformGetSelfOnboardingVerifyAPI: auxBackendURL + '/api/platform/get/selfOnboardingVerify/',
  platformPostSelfOnboardingVerifyAPI: auxBackendURL + '/api/platform/post/selfOnboardingVerify/',
  platformPostSelfOnboardingBrandingAPI:
    auxBackendURL + '/api/platform/post/selfOnboardingBranding/',
  platformPostSelfOnboardingValuesSetupAPI:
    auxBackendURL + '/api/platform/post/selfOnboardingValuesSetup/',
  platformPostSelfOnboardingGettingStartedCompleteAPI:
    auxBackendURL + '/api/platform/post/selfOnboardingGettingStartedComplete/',
  platformPostSelfOnboardingGettingStartedAssignAgreementProjectAPI:
    auxBackendURL + '/api/platform/post/selfOnboardingGettingStartedAssignAgreementProject/',
  platformPostPlus1RecognitionAPI: auxBackendURL + '/api/platform/post/plus1Recognition/',

  // News
  platformGetNewsFeedAPI: auxBackendURL + '/api/platform/get/newsFeed/',
  platformGetNewsByIdAPI: auxBackendURL + '/api/platform/get/newsById/',
  platformGetNewsCommsListAPI: auxBackendURL + '/api/platform/get/newsCommsList/',
  platformPostNewsAPI: auxBackendURL + '/api/platform/post/news/',
  platformPutNewsAPI: auxBackendURL + '/api/platform/put/news/',
  platformPutSoftDeleteNewsAPI: auxBackendURL + '/api/platform/put/softDeleteNews/',
  platformPostMarkNewsReadAPI: auxBackendURL + '/api/platform/post/markNewsRead/',
  platformPostLikeNewsAPI: auxBackendURL + '/api/platform/post/likeNews/',
  platformGetNewsCommentsAPI: auxBackendURL + '/api/platform/get/newsComments/',
  platformPostNewsCommentAPI: auxBackendURL + '/api/platform/post/newsComment/',
  platformPutSoftDeleteNewsCommentAPI: auxBackendURL + '/api/platform/put/softDeleteNewsComment/',

  // Rewards
  platformGetRewardsDataForCompanyAPI: auxBackendURL + '/api/platform/get/rewardsDataForCompany/',
  platformPostCashRewardOutForEmployeeAPI:
    auxBackendURL + '/api/platform/post/cashRewardOutForEmployee/',

  // Organigram
  platformGetOrganigramAPI: auxBackendURL + '/api/platform/get/organigram/',
  platformGetOrganigramChildrenAPI: auxBackendURL + '/api/platform/get/organigramChildren/',
  platformGetOrgOfManagerAPI: auxBackendURL + '/api/platform/get/orgOfManager/',
  platformPostNewEmployeeAPI: auxBackendURL + '/api/platform/post/newEmployee/',
  platformPutEmployeeAPI: auxBackendURL + '/api/platform/put/employee/',
  platformDeleteEmployeeAPI: auxBackendURL + '/api/platform/delete/employee/',
  platformGetOrganigramCSVAPI: auxBackendURL + '/api/platform/get/organigramCSV/',
  platformPostOrganigramReorganizeSegmentPreviewAPI:
    auxBackendURL + '/api/platform/post/organigramReorganizeSegmentPreview/',
  platformPostOrganigramReorganizationAnalyzeAPI:
    auxBackendURL + '/api/platform/post/organigramReorganizationAnalyze/',
  platformPostOrganigramReorganizationAPI:
    auxBackendURL + '/api/platform/post/organigramReorganization/',
  platformGetOrganigramReorganizationHistoryAPI:
    auxBackendURL + '/api/platform/get/organigramReorganizationHistory/',
  platformGetEmployeePermissionsAPI: auxBackendURL + '/api/platform/get/employeePermissions/',
  platformPutEmployeePermissionsAPI: auxBackendURL + '/api/platform/put/employeePermissions/',
  platformPutEmployeeCredentialRolesAPI: auxBackendURL + '/api/platform/put/employeeCredentialRoles/',
  platformDeleteEmployeePermissionsAPI: auxBackendURL + '/api/platform/delete/employeePermissions/',
  platformPostHrbpModuleScopePreviewAPI: auxBackendURL + '/api/platform/post/hrbpModuleScopePreview/',

  // Onboarding
  platformGetOnboardingEligibleCandidatesAPI:
    auxBackendURL + '/api/platform/get/onboardingEligibleCandidates/',
  platformGetOnboardingPeopleOptionsAPI:
    auxBackendURL + '/api/platform/get/onboardingPeopleOptions/',
  platformGetOnboardingSubjectProfileAPI:
    auxBackendURL + '/api/platform/get/onboardingSubjectProfile/',
  platformGetOnboardingPlanForEmployeeAPI:
    auxBackendURL + '/api/platform/get/onboardingPlanForEmployee/',
  platformGetOnboardingPlanTemplatesAPI:
    auxBackendURL + '/api/platform/get/onboardingPlanTemplates/',
  platformGetSpecificOnboardingPlanAPI: auxBackendURL + '/api/platform/get/onboardingPlan/',
  platformPostOnboardingPlanAPI: auxBackendURL + '/api/platform/post/onboardingPlan/',
  platformDeleteOnboardingPlanAPI: auxBackendURL + '/api/platform/delete/onboardingPlan/',
  platformPostOnboardingTemplateAPI: auxBackendURL + '/api/platform/post/onboardingTemplate/',
  platformPutOnboardingTemplateAPI: auxBackendURL + '/api/platform/put/onboardingTemplate/',
  platformPostOnboardingPlanSubsectionCompleteAPI:
    auxBackendURL + '/api/platform/post/onboardingPlanSubsectionComplete/',
  platformGetOnboardingReportingDataAPI:
    auxBackendURL + '/api/platform/get/onboardingReportingData/',
  platformGetOnboardingReportingStatusFunnelAPI:
    auxBackendURL + '/api/platform/get/onboardingReportingStatusFunnel/',
  platformGetOnboardingReportingCompletionAPI:
    auxBackendURL + '/api/platform/get/onboardingReportingCompletion/',
  platformGetOnboardingReportingAgingAPI:
    auxBackendURL + '/api/platform/get/onboardingReportingAging/',
  platformGetOnboardingReportingBuddyCoverageAPI:
    auxBackendURL + '/api/platform/get/onboardingReportingBuddyCoverage/',
  platformGetOnboardingReportingSurveyThemesAPI:
    auxBackendURL + '/api/platform/get/onboardingReportingSurveyThemes/',
  platformGetOnboardingReportingLikertDistributionAPI:
    auxBackendURL + '/api/platform/get/onboardingReportingLikertDistribution/',
  platformDeleteOnboardingPlanTemplateAPI:
    auxBackendURL + '/api/platform/delete/onboardingTemplate/',

  // Career path
  platformGetCareerJobGuidelinesAPI: auxBackendURL + '/api/platform/get/careerJobGuidelines/',
  platformPostCareerJobGuidelineAPI: auxBackendURL + '/api/platform/post/careerJobGuideline/',
  platformPutCareerJobGuidelineAPI: auxBackendURL + '/api/platform/put/careerJobGuideline/',
  platformDeleteCareerJobGuidelineAPI: auxBackendURL + '/api/platform/delete/careerJobGuideline/',
  platformGetCareerPathForEmployeeAPI: auxBackendURL + '/api/platform/get/careerPathForEmployee/',
  platformPostCareerPathForEmployeeAPI: auxBackendURL + '/api/platform/post/careerPathForEmployee/',
  platformPutCareerPathForEmployeeAPI: auxBackendURL + '/api/platform/put/careerPathForEmployee/',

  platformPostCareerConversationAPI: auxBackendURL + '/api/platform/post/careerConversation/',

  // Development Plans
  platformGetDevelopmentPlanAPI: auxBackendURL + '/api/platform/get/developmentPlan/',
  platformGetDevelopmentPlanByIDAPI: auxBackendURL + '/api/platform/get/developmentPlanByID/',
  platformPostDevelopmentPlanAPI: auxBackendURL + '/api/platform/post/developmentPlan/',
  platformPostCreateDevelopmentPlanAPI: auxBackendURL + '/api/platform/post/createDevelopmentPlan/',
  platformPutDevelopmentPlanAPI: auxBackendURL + '/api/platform/put/developmentPlan/',
  platformDeleteDevelopmentPlanAPI: auxBackendURL + '/api/platform/delete/developmentPlan/',
  platformPostGenerateDevelopmentPlanAPI: auxBackendURL + '/api/platform/post/generateDevelopmentPlan/',
  platformGetDevelopmentPlanUpdatesAPI: auxBackendURL + '/api/platform/get/developmentPlanUpdates/',
  platformPostDevelopmentPlanUpdateAPI: auxBackendURL + '/api/platform/post/developmentPlanUpdate/',
  platformPutDevelopmentPlanUpdateAPI: auxBackendURL + '/api/platform/put/developmentPlanUpdate/',
  platformDeleteDevelopmentPlanUpdateAPI: auxBackendURL + '/api/platform/delete/developmentPlanUpdate/',
  platformPostDevelopmentDashboardAPI: auxBackendURL + '/api/platform/post/developmentDashboard/',
  platformGetDevelopmentTeamAPI: auxBackendURL + '/api/platform/get/developmentTeam/',
  platformGetPerformanceTeamAPI: auxBackendURL + '/api/platform/get/performanceTeam/',
  platformGetCareerTeamAPI: auxBackendURL + '/api/platform/get/careerTeam/',
  platformGetCareerSubjectProfileAPI: auxBackendURL + '/api/platform/get/careerSubjectProfile/',
  platformGetCareerReportingRosterAPI: auxBackendURL + '/api/platform/get/careerReportingRoster/',
  platformGetCareerReportingPathCoverageAPI:
    auxBackendURL + '/api/platform/get/careerReportingPathCoverage/',
  platformGetCareerReportingActivityFreshnessAPI:
    auxBackendURL + '/api/platform/get/careerReportingActivityFreshness/',
  platformGetGoalsTeamAPI: auxBackendURL + '/api/platform/get/goalsTeam/',
  platformGetDevelopmentMemberPlansAPI: auxBackendURL + '/api/platform/get/developmentMemberPlans/',
  platformGetDevelopmentPlansSharedWithViewerAPI:
    auxBackendURL + '/api/platform/get/developmentPlansSharedWithViewer/',
  platformGetDevelopmentPlanTemplatesAPI:
    auxBackendURL + '/api/platform/get/developmentPlanTemplates/',
  platformPostDevelopmentPlanTemplateAPI:
    auxBackendURL + '/api/platform/post/developmentPlanTemplate/',
  platformPutDevelopmentPlanTemplateAPI:
    auxBackendURL + '/api/platform/put/developmentPlanTemplate/',
  platformDeleteDevelopmentPlanTemplateAPI:
    auxBackendURL + '/api/platform/delete/developmentPlanTemplate/',

  // Learning
  platformGetLearningCoursesCatalogueAPI:
    auxBackendURL + '/api/platform/get/learningCoursesCatalogue/',
  platformGetLearningCourseDetailsAPI: auxBackendURL + '/api/platform/get/learningCourseDetails/',
  platformPutLearningCourseAPI: auxBackendURL + '/api/platform/put/learningCourse/',
  platformPostLearningCourseAssignmentAPI:
    auxBackendURL + '/api/platform/post/learningCourseAssignment/',
  platformGetLearningEmployeeAssignmentsAPI:
    auxBackendURL + '/api/platform/get/learningEmployeeAssignments/',
  platformGetLearningCourseBlocksAPI: auxBackendURL + '/api/platform/get/learningCourseBlocks/',
  platformPostLearningCourseBlockAPI: auxBackendURL + '/api/platform/post/learningCourseBlock/',
  platformPutLearningCourseBlockAPI: auxBackendURL + '/api/platform/put/learningCourseBlock/',
  platformDeleteLearningCourseBlockAPI: auxBackendURL + '/api/platform/delete/learningCourseBlock/',
  platformGetLearningCourseProgressAPI: auxBackendURL + '/api/platform/get/learningCourseProgress/',
  platformPostLearningCourseProgressAPI:
    auxBackendURL + '/api/platform/post/learningCourseProgress/',
  platformPutLearningCourseProgressAPI: auxBackendURL + '/api/platform/put/learningCourseProgress/',
  platformPutLearningBlockProgressAPI: auxBackendURL + '/api/platform/put/learningBlockProgress/',

  // Goals
  platformGetGoalsAPI: auxBackendURL + '/api/platform/get/goals/',
  platformGetGoalAPI: auxBackendURL + '/api/platform/get/goal/',
  platformPostGoalAPI: auxBackendURL + '/api/platform/post/goal/',
  platformPostGoalReviewQualityAPI: auxBackendURL + '/api/platform/post/goals/reviewQuality/',
  platformPutGoalAPI: auxBackendURL + '/api/platform/put/goal/',
  platformDeleteGoalAPI: auxBackendURL + '/api/platform/delete/goal/',

  // Recruitment (Claude-first)
  platformGetRecruitmentJobsAPI: auxBackendURL + '/api/platform/get/recruitmentJobs/',
  platformGetRecruitmentJobAPI: auxBackendURL + '/api/platform/get/recruitmentJob/',
  platformPostRecruitmentJobAPI: auxBackendURL + '/api/platform/post/recruitmentJob/',
  platformPutRecruitmentJobAPI: auxBackendURL + '/api/platform/put/recruitmentJob/',
  platformGetRecruitmentCandidatesAPI: auxBackendURL + '/api/platform/get/recruitmentCandidates/',
  platformGetRecruitmentCandidateAPI: auxBackendURL + '/api/platform/get/recruitmentCandidate/',
  platformPostRecruitmentCandidateAPI: auxBackendURL + '/api/platform/post/recruitmentCandidate/',
  platformPutRecruitmentCandidateAPI: auxBackendURL + '/api/platform/put/recruitmentCandidate/',
  platformGetRecruitmentApplicationsAPI: auxBackendURL + '/api/platform/get/recruitmentApplications/',
  platformGetRecruitmentApplicationAPI: auxBackendURL + '/api/platform/get/recruitmentApplication/',
  platformPostRecruitmentApplicationAPI: auxBackendURL + '/api/platform/post/recruitmentApplication/',
  platformGetRecruitmentCareersSettingsAPI:
    auxBackendURL + '/api/platform/get/recruitmentCareersSettings/',
  platformPutRecruitmentCareersSettingsAPI:
    auxBackendURL + '/api/platform/put/recruitmentCareersSettings/',
  platformGetGoalUpdatesAPI: auxBackendURL + '/api/platform/get/goalUpdates/',
  platformPostGoalUpdateAPI: auxBackendURL + '/api/platform/post/goalUpdate/',
  platformPutGoalUpdateAPI: auxBackendURL + '/api/platform/put/goalUpdate/',
  platformDeleteGoalUpdateAPI: auxBackendURL + '/api/platform/delete/goalUpdate/',
  platformGetGoalCommentsAPI: auxBackendURL + '/api/platform/get/goalComments/',
  platformPostGoalCommentAPI: auxBackendURL + '/api/platform/post/goalComment/',
  platformPutGoalCommentAPI: auxBackendURL + '/api/platform/put/goalComment/',
  platformDeleteGoalCommentAPI: auxBackendURL + '/api/platform/delete/goalComment/',
  platformGetGoalSubscriptionsAPI: auxBackendURL + '/api/platform/get/goalSubscriptions/',
  platformPostGoalSubscriptionAPI: auxBackendURL + '/api/platform/post/goalSubscription/',
  platformDeleteGoalSubscriptionAPI: auxBackendURL + '/api/platform/delete/goalSubscription/',
  platformGetGoalCategoriesAPI: auxBackendURL + '/api/platform/get/goalCategories/',
  platformGetGoalSettingsAPI: auxBackendURL + '/api/platform/get/goalSettings/',
  platformPutGoalSettingsAPI: auxBackendURL + '/api/platform/put/goalSettings/',
  platformGetGoalHierarchyAPI: auxBackendURL + '/api/platform/get/goalHierarchy/',

  // Settings
  platformGetCompanyPreferencesAPI: auxBackendURL + '/api/platform/get/companyPreferences/',
  platformGetKAICompanyPreferencesAPI: auxBackendURL + '/api/platform/get/kaiCompanyPreferences/',
  platformPostUploadKAIFileAPI: auxBackendURL + '/api/platform/post/uploadKAIFile/',

  platformPostSaveSettingsAPI: auxBackendURL + '/api/platform/post/saveSettings/',
  platformGetEmployeePreferredLanguageAPI:
    auxBackendURL + '/api/platform/get/employeePreferredLanguage/',
  platformPatchEmployeePreferredLanguageAPI:
    auxBackendURL + '/api/platform/patch/employeePreferredLanguage/',
  platformPatchCompanyDisplayNameAPI:
    auxBackendURL + '/api/platform/patch/companyDisplayName/',

  // Culture
  platformGetCultureDriversMonthlyReportingAPI:
    auxBackendURL + '/api/platform/post/cultureDriversMonthlyReporting/',
  platformGetCulturePerformanceKPIsAPI:
    auxBackendURL + '/api/platform/post/culturePerformanceKPIs/',

  // Bot // KAI
  platformSubmitFeedbackFormAPI: auxBackendURL + '/api/platform/post/submitFeedbackForm/',
  platformPostKAIMessageAPI: auxBackendURL + '/api/platform/post/kaiMessage/',
  platformGetIndexKAIDocumentsAPI: auxBackendURL + '/api/platform/get/indexKAICompanyDocuments/',

  // Impersonation
  platformGetEmployeesOfCompanyAPI: auxBackendURL + '/api/platform/get/employeesOfCompany/',
  platformGetEmployeeBasicInfoAPI: auxBackendURL + '/api/platform/get/employee/basicInfo/',
  platformGetCompanyEmployeesPageAPI: auxBackendURL + '/api/platform/get/companyEmployeesPage',
  platformGetEmployeesOfManagerAPI: auxBackendURL + '/api/platform/get/employeesOfManager/',
  platformGetMeetingsByEmployeeAPI: auxBackendURL + '/api/platform/get/meetingsOfEmployee/',
  platformPostUploadFile: auxBackendURL + '/api/platform/post/uploadFile/',
  platformGetDownloadFileAPI: auxBackendURL + '/api/platform/get/downloadFile/',
  platformGetPublicURLForFileAPI: auxBackendURL + '/api/platform/get/publicURLForFile/',
  platformGetKeyKPIsForEmployeeAPI: auxBackendURL + '/api/platform/get/keyKPIsForEmployee/',

  // PLG
  platformPostSlackAuthResponseAPI: auxBackendURL + '/api/platform/post/slackAuthResponse/',

  // ------------------------------------------------------------------------------------------
  // API - Product Led Growth
  // ------------------------------------------------------------------------------------------
  slackExchangeTokenForCodeAPI: 'https://slack.com/api/oauth.v2.access',
  slackClientSecret: import.meta.env.VITE_SLACK_CLIENT_SECRET,
  slackClientId: import.meta.env.VITE_SLACK_CLIENT_ID,

  // ------------------------------------------------------------------------------------------
  // Company configurations
  // ------------------------------------------------------------------------------------------
  headerColor: 'bg-green-200',
  secondaryColor: 'bg-gray-200',
  companyFavicon: 'https://www.dropbox.com/s/iqbutdfpt2gd1xn/TaxDown_favicon%402x.png?raw=1',
  companyLogoSmall:
    'https://kincode-prod-public.s3.eu-central-1.amazonaws.com/Logo-Kincode-h-navbar_dev.png',
  companyLogoLarge:
    'https://kincode-prod-public.s3.eu-central-1.amazonaws.com/Logo-Kincode-h-navbar_dev.png',
  companyColor: '#BBF7D0', // Green-200
  companySecondaryColor: '#BFC6FF', // Purple
  companyTerciaryColor: '#FFBF76', // Yellow

  // --------------------------------------------------------------------------------------------------------------
  // Customer integrations
  // --------------------------------------------------------------------------------------------------------------
  // Kincode App multitenant
  azureSandboxSecret: import.meta.env.VITE_AZURE_SECRET_ID,
  azureSandboxClientID: import.meta.env.VITE_AZURE_CLIENT_ID,
  azureSandboxTenant: import.meta.env.VITE_AZURE_TENANT_ID,
  azureSandboxResource: '9a5d1bb8-595e-4dd6-8e2d-b1a88921d704',

  azureCloudInstance: 'https://login.microsoftonline.com',
  azureAuthorizationEndpoint: 'https://login.microsoftonline.com/common',
  azureGraphEndpoint: 'https://graph.microsoft.com',
  integrationCallbackURL: auxDevEnvironment
    ? 'http://localhost:3000'
    : 'https://platform.kincode.app',

  /**
   * Google Identity Services throws `Missing required parameter client_id.` when
   * `GoogleOAuthProvider` is given an empty value, which the ErrorBoundary turns into a full
   * login-screen crash. Environments without Google SSO (CI/E2E, local dev) get an inert but
   * well-formed ID so the provider stays quiet; `googleSSOEnabled` hides the SSO button instead.
   */
  googleClientID:
    import.meta.env.VITE_GOOGLE_CLIENT_ID || 'sso-disabled.apps.googleusercontent.com',

  googleSSOEnabled: Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID),
  microsoftSsoEnabled: Boolean(import.meta.env.VITE_AZURE_CLIENT_ID),

  amplitudeAPIKey: import.meta.env.VITE_AMPLITUDE_API_KEY,

  // --------------------------------------------------------------------------------------------------------------
  // Toaster
  // --------------------------------------------------------------------------------------------------------------
  // Toaster error codes
  successToaster: 'bg-green-500',
  successToasterDark: 'bg-green-400',
  warningToaster: 'bg-yellow-500',
  warningToasterDark: 'bg-yellow-400',
  errorToaster: 'bg-red-500',
  errorToasterDark: 'bg-red-400',
  infoToaster: 'bg-blue-500',
  infoToasterDark: 'bg-blue-400',

  toasterTimeout: 5000, // Increased from 3000ms to 5000ms for better readability
  saveTimer: 60000,

  // --------------------------------------------------------------------------------------------------------------
  // Feedback popup
  // --------------------------------------------------------------------------------------------------------------
  feedbackPopupChance: auxDevEnvironment ? 100 : 60,
  feedbackPopupChancePunch: auxDevEnvironment ? 100 : 30,

  // --------------------------------------------------------------------------------------------------------------
  // Notifications
  // --------------------------------------------------------------------------------------------------------------

  // Notification types
  // Satisfaction
  notificationsSurvey: 'NOTIFICATION_SURVEY',
  // Performance reviews
  notificationsPeerFeedback: 'NOTIFICATION_FEEDBACK',
  notificationsSelfPerformanceReview: 'NOTIFICATION_SELF_PERFORMANCE_REVIEW',
  notificationsManagerPerformanceReview: 'NOTIFICATION_MANAGER_PERFORMANCE_REVIEW',
  notificationsRequestFeedbackPending: 'NOTIFICATION_REQUEST_FEEDBACK_PENDING',
  notificationsFeedbackOfManager: 'NOTIFICATION_FEEDBACK_OF_MANAGER',
  // Development
  notificationsActionPlanForPerformaceReview:
    'NOTIFICATION_MANAGER_PERFORMANCE_REVIEW_DEVELOPMENT_PLAN',
  // Calibration
  notificationsCalibrationForManagers: 'NOTIFICATION_CALIBRATION_FOR_MANAGERS',
  // Recognition
  notificationsRecognitionForManagers: 'NOTIFICATION_RECOGNITION_FOR_MANAGERS',
  notificationsRecognitionForPeers: 'NOTIFICATION_RECOGNITION_FOR_PEERS',
  // Meetings
  notificationsOneOnOne: 'NOTIFICATION_ONE_ON_ONE',
  // Onboarding
  notificationsOnboardingSelfPlan: 'NOTIFICATION_ONBOARDING_SELF_PLAN',
  notificationsOnboardingPlanManager: 'NOTIFICATION_ONBOARDING_PLAN_MANAGER',
  notificationsOnboardingPlanEmployee3P: 'NOTIFICATION_ONBOARDING_PLAN_EMPLOYEE_3P',

  notificationsTimeOffRequest: 'NOTIFICATION_TIME_OFF_REQUEST',

  // EDQ
  notificationsEDQMissingManager: 'NOTIFICATION_EDQ_MISSING_MANAGER',

  // Statuses
  notificationStatusUnread: 'Unread',
  notificationStatusRead: 'Read',
  notificationStatusRejected: 'Rejected',
  notificationStatusCompleted: 'Completed',
  notificationStatusExpired: 'Expired',

  // Priorities
  priorityPulseSurvey: 0,

  priorityPeerFeedback: 0,
  prioritySelfPerformanceReview: 0,
  priorityManagerPerformanceReview: 0,

  priorityActionPlanForPerformaceReview: 0,

  priorityOneOnOne: 0,

  priorityCalibration: 0,

  priorityOnboardingSelfPlan: 0,
  priorityOnboardingPlanManager: 0,
  priorityOnboardingPlanEmployee3P: 0,

  priorityTimeOff: 0,

  // --------------------------------------------------------------------------------------------------------------
  // Goals
  // --------------------------------------------------------------------------------------------------------------
  // Goal Status Types
  goalStatusTypes: {
    NOT_STARTED: 'not-started',
    ON_TRACK: 'on-track',
    RESCHEDULED: 'rescheduled',
    AT_RISK: 'at-risk',
    WILL_NOT_BE_MET: 'will-not-be-met',
    NOT_MET: 'not-met',
    COMPLETED: 'achieved', // Note: 'achieved' is the database value, but 'completed' is the display name
  },

  // --------------------------------------------------------------------------------------------------------------
  // OneOnOnes
  // --------------------------------------------------------------------------------------------------------------
  oneOnOneTypeMeeting: 'Meeting',
  oneOnOneTypeDevelopmentPlan: 'DevelopmentPlan',
  oneOnOneTypeCareerConversation: 'CareerConversation',

  // --------------------------------------------------------------------------------------------------------------
  // Time off
  // --------------------------------------------------------------------------------------------------------------
  daysOffLeaveTypeVacation: 'Vacation',
  daysOffLeaveTypeSick: 'Sick',
  daysOffLeaveTypePersonal: 'Personal',
  daysOffLeaveTypeMaternity: 'Maternity',
  daysOffLeaveTypeCarryOver: 'Carry over',
  daysOffLeaveTypeAward: 'Award',
  daysOffLeaveTypeUnpaid: 'Unpaid',
  daysOffLeaveTypeBereavement: 'Bereavement',
  daysOffLeaveTypeOther: 'Other',
  daysOffLeaveTypeWFH: 'WFH',
  daysOffLeaveTypeWFHSick: 'WFH Sick',
  daysOffLeaveTypeWedding: 'Wedding',

  daysOffStatusPending: 'Pending',
  daysOffStatusApproved: 'Approved',
  daysOffStatusRejected: 'Rejected',
  daysOffStatusCancelled: 'Cancelled',

  // --------------------------------------------------------------------------------------------------------------
  // Punch In/out
  // --------------------------------------------------------------------------------------------------------------
  punchInOutDefaultPolicy: {
    settings: {
      in: '09:00',
      out: '18:00',
      restingTime: 1,
      totalHours: 8,
    },
  },

  // --------------------------------------------------------------------------------------------------------------
  // Onboarding plans
  // --------------------------------------------------------------------------------------------------------------
  onboardingPlanStatusDraft: 'Draft',
  onboardingPlanStatusPublished: 'Published',
  onboardingPlanStatusNotStarted: 'Not Started',
  onboardingPlanStatusInProgress: 'In Progress',
  onboardingPlanStatusCompleted: 'Completed',

  // --------------------------------------------------------------------------------------------------------------
  // KPI Cards
  // --------------------------------------------------------------------------------------------------------------
  KPIGraphAccelerator: 'GraphAccelerator',

  // --------------------------------------------------------------------------------------------------------------
  // Audit Logs types
  // --------------------------------------------------------------------------------------------------------------
  auditLogTypeLogin: 'LOGIN',
  auditLogTypeLogout: 'LOGOUT',
  auditLogTypePasswordChange: 'PASSWORD_CHANGE',
  auditLogTypePasswordReset: 'PASSWORD_RESET',
  auditLogTypePasswordResetRequest: 'PASSWORD_RESET_REQUEST',

  auditLogTypeUserFeedback: 'USER_FEEDBACK',

  auditLogTypeRecognitionPosted: 'RECOGNITION_POSTED',
  auditLogTypeRecognitionReceived: 'RECOGNITION_RECEIVED',

  // Security
  auditLogTypeLoginAttemptWOUsernameOrPassword: 'LOGIN_WO_USERNAME_OR_PASSWORD',
  auditLogTypeLoginNonExistingUsername: 'LOGIN_NONEXISTING_USERNAME',
  auditLogTypeLoginIncorrectPassword: 'LOGIN_INCORRECT_PASSWORD',
  auditLogTypePasswordResetNonexistingUsername: 'PASSWORD_RESET_NONEXISTING_USERNAME',
  auditLogTypeNastyCrossCompanyRequest: 'NASTY_CROSS_COMPANY_REQUEST',
  auditLogTypeNastyCrossEmployeeRequest: 'NASTY_CROSS_EMPLOYEE_REQUEST',

  // --------------------------------------------------------------------------------------------------------------
  // Tickets
  // --------------------------------------------------------------------------------------------------------------
  ticketStatusOpen: 'Open',
  ticketStatusAssigned: 'Assigned',
  ticketStatusInProgress: 'In Progress',
  ticketStatusOnHold: 'On Hold',
  ticketStatusWaitingForResponse: 'Waiting for response',
  ticketStatusResolved: 'Resolved',
  ticketStatusClosed: 'Closed',
  ticketStatusReopened: 'Reopened',
  ticketStatusEscalated: 'Escalated',
  ticketStatusDeleted: 'Deleted',

  // --------------------------------------------------------------------------------------------------------------
  // Pulse defaults
  // --------------------------------------------------------------------------------------------------------------
  pulseDefaultDrivers: [
    'pulse.drivers.satisfaction', // Satisfaction
    'pulse.drivers.purpose', // Purpose & meaning
    'pulse.drivers.engagement', // Engagement
    'pulse.drivers.leadership', // Leadership & communication
    'pulse.drivers.growth.title', // Growth
    'pulse.drivers.inclusion', // Inclusion & fairness
    'pulse.drivers.manager', // Management support
    'pulse.drivers.teamwork', // Teamwork
    'pulse.drivers.recognition.title', // Recognition & rewards
    'pulse.drivers.belonging.title', // Belonging
    'pulse.drivers.opinion', // Freedom of Opinion
    'pulse.drivers.innovation', // Innovation & agility
    'pulse.drivers.worklife', // Work-life balance
    'pulse.drivers.resources', // Resources & tools
    'pulse.drivers.organizational', // Organizational fit
  ],

  // --------------------------------------------------------------------------------------------------------------
  // File input types
  // --------------------------------------------------------------------------------------------------------------
  maxFileSize: 15 * 1024 * 1024, // 15MB = 15 * 1024 * 1024
  allowedTypes: [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  allowedTypesPDF: ['application/pdf'],
  allowedTypesImages: ['image/jpeg', 'image/png'],
  allowedTypesCSV: [
    'text/csv',
    'text/plain',
    'application/csv',
    'application/octet-stream',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],

  // --------------------------------------------------------------------------------------------------------------
  // Styles
  // --------------------------------------------------------------------------------------------------------------
  formDropdownStyle: {
    control: (provided, state) => ({
      ...provided,
      borderRadius: '8px',
      borderColor: state.isFocused ? companyColor : '#D1D5DB', // Tailwind colors: gray-600 and gray-300
      boxShadow: state.isFocused
        ? '0 0 0 3px ' + companyColor
        : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        borderColor: state.isFocused ? '#4B5563' : '#9CA3AF', // Tailwind colors: gray-600 and gray-400
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: '8px',
      border: '1px solid #D1D5DB', // Tailwind colors: gray-300
      // borderColor: state.isFocused ? '#D1D5DB' : '#D1D5DB', // Tailwind colors: gray-600 and gray-300
      boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
      textAlign: 'left',
      zIndex: 55,
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#000000' : '#1F2937', // Tailwind colors: white and gray-800
      backgroundColor: state.isSelected
        ? companySecondaryColor
        : state.isFocused
          ? companyColor
          : '#FFFFFF',
    }),
  },

  widgetDropdownStyle: {
    control: (provided, state) => ({
      ...provided,
      borderRadius: '8px',
      backgroundColor: 'none',
      border: 'none',
      cursor: 'pointer',
      // borderColor: state.isFocused ? companyColor : '#D1D5DB', // Tailwind colors: gray-600 and gray-300
      boxShadow: state.isFocused ? '0 0 0 3px ' + companyColor : provided.boxShadow,
      '&:hover': {
        borderColor: state.isFocused ? '#4B5563' : '#9CA3AF', // Tailwind colors: gray-600 and gray-400
        backgroundColor: '#e2e8f0',
        boxShadow: '0 0 8px 2px #e2e8f0',
      },
      ':focus': {
        // backgroundColor: "#e2e8f0",
        // boxShadow: '0 0 8px 2px #e2e8f0',
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: '8px',
      // border: '1px solid #D1D5DB', // Tailwind colors: gray-300
      // borderColor: state.isFocused ? '#D1D5DB' : '#D1D5DB', // Tailwind colors: gray-600 and gray-300
      // boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#000000' : '#1F2937', // Tailwind colors: white and gray-800
      backgroundColor: state.isSelected
        ? companySecondaryColor
        : state.isFocused
          ? companyColor
          : '#FFFFFF',
    }),
  },

  graphsColor5: '#80e0b0', // esti: #00E5B1, // carol: #00E5B1,
  graphsColor4: '#b9e3cf', // esti: #81BF82, // carol:rgba(0, 229, 176, 0.54),
  graphsColor3: '#f2d694', // esti: #FFED8A, // carol: rgba(0, 123, 84, 0.12)
  graphsColor2: '#f7a8a1', // esti: #FFBF76, // carol:rgba(255, 179, 179, 0.8)
  graphsColor1: '#f28b82', // esti: #FD9163, // carol:rgba(255, 0, 0, 0.51)
  graphsColor0: '#FF6384', // esti: #FF6384,
  graphsColorNeutral: '#BFC6FF', // color: #BFC6FF,
  graphsIdleColor: '#F1F2F5', // color: #D1D5DB, // #F1F2F5
  //  : ['#f28b82', '#f7a8a1', '#f2d694', '#b9e3cf',  '#80e0b0'];chatgpt

  // --------------------------------------------------------------------------------------------------------------
  defaultEmployeeConfig: {
    personalFields: [
      {
        name: 'name',
        label: 'name',
        type: 'text',
        required: true,
        placeholder: 'organigram.employee.name.placeholder',
      },
      {
        name: 'surname',
        label: 'surname',
        type: 'text',
        required: true,
        placeholder: 'organigram.employee.surname.placeholder',
      },
      {
        name: 'email',
        label: 'email',
        type: 'text',
        required: true,
        placeholder: 'organigram.employee.email.placeholder',
        tooltip: 'organigram.employee.email.info',
      },
      {
        name: 'hireDate',
        label: 'hire-date',
        type: 'date',
        required: false,
        placeholder: 'organigram.employee.hireDate.placeholder',
        tooltip: 'organigram.employee.hireDate.info',
      },
      {
        name: 'birthday',
        label: 'birthday',
        type: 'date',
        required: false,
        placeholder: 'organigram.employee.birthday.placeholder',
        tooltip: 'organigram.employee.birthday.info',
      },
    ],
    organigramFields: [
      {
        name: 'managerID',
        label: 'manager',
        type: 'select',
        required: true,
        placeholder: 'organigram.employee.manager.placeholder',
        tooltip: 'organigram.employee.manager.info',
      },
      {
        name: 'area',
        label: 'area',
        type: 'select',
        required: false,
        placeholder: 'organigram.employee.area.placeholder',
      },
      {
        name: 'subarea',
        label: 'subarea',
        type: 'select',
        required: false,
        placeholder: 'organigram.employee.subarea.placeholder',
      },
      {
        name: 'title',
        label: 'title',
        type: 'select',
        required: false,
        placeholder: 'organigram.employee.title.placeholder',
      },
    ],
    careerFields: [
      {
        name: 'role',
        label: 'role',
        type: 'select',
        required: false,
        placeholder: 'organigram.employee.role.placeholder',
      },
      {
        name: 'level',
        label: 'level',
        type: 'select',
        required: false,
        placeholder: 'organigram.employee.level.placeholder',
      },
    ],
    onboardingFields: [
      {
        name: 'onboarding',
        label: 'onboarding',
        type: 'text',
        required: false,
        placeholder: 'organigram.employee.onboarding.placeholder',
      },
    ],
    timeOffFields: [
      {
        name: 'country',
        label: 'country',
        type: 'text',
        required: false,
        placeholder: 'organigram.employee.timeoff.placeholder',
      },
      {
        name: 'region',
        label: 'region',
        type: 'text',
        required: false,
        placeholder: 'organigram.employee.timeoff.placeholder',
      },
      {
        name: 'city',
        label: 'city',
        type: 'text',
        required: false,
        placeholder: 'organigram.employee.timeoff.placeholder',
      },
    ],
  },
};

export default Configs;
